import { useRef, useLayoutEffect, DependencyList, MutableRefObject } from 'react';
// Custom hook from: https://dev.to/n8tb1t/tracking-scroll-position-with-react-hooks-3bbj

const isClient = typeof window !== `undefined`;

interface IPositionProps {
  x: number;
  y: number;
}

interface IScrollProps {
  prevPos: IPositionProps;
  currPos: IPositionProps;
}

type ElementRef = MutableRefObject<HTMLElement | undefined>;

interface GetScrollProps {
  element?: ElementRef;
  useWindow?: boolean;
}

const getScrollPosition = ({ element, useWindow }: GetScrollProps) => {
  if (!isClient) return { x: 0, y: 0 };

  const target = element ? element.current : document.body;
  const position = target.getBoundingClientRect();

  return useWindow
    ? { x: window.scrollX, y: window.scrollY }
    : { x: position.left, y: position.top };
};

export const useScrollPosition = (
  effect: (props: IScrollProps) => void,
  deps?: DependencyList,
  element?: ElementRef,
  useWindow?: boolean,
  wait?: number,
): void => {
  const position = useRef(getScrollPosition({ useWindow }));

  let throttleTimeout = null;

  const callBack = () => {
    const currPos = getScrollPosition({ element, useWindow });
    effect({ prevPos: position.current, currPos });
    position.current = currPos;
    throttleTimeout = null;
  };

  useLayoutEffect(() => {
    const handleScroll = () => {
      if (wait) {
        if (throttleTimeout === null) {
          throttleTimeout = setTimeout(callBack, wait);
        }
      } else {
        callBack();
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, deps);
};
